import React, { useEffect, useState } from 'react';
import { Item, CartItemSelection, pedidoTypeMap, getItemPrice, getSelectionItemPriceModifier, getModifierOptionPriceModifier } from './types';
import { getAllBuiltMenusByLocalIdV2 } from '../../../../../crud/menu_api/menus';
import { useStrictStock } from './hooks/useStrictStock';
import { stockMessages } from './hooks/strictStockMessages';
import { createPaymentReceiptByMesaIdV2 } from '../../../../../crud/payments_api/payment_receipts';
import NewOrderItemSelector from './components/NewOrderSelector/NewOrderSelector';
import GlobalSearchBar from './components/GlobalSearchBar/GlobalSearchBar';
import Cart from './components/Cart/Cart';
import MiniLoadingSpinner from '../../../../../components/Spinners/MiniLoadingSpinner';
import SelectorModal from './components/NewOrderSelector/components/SelectionModal/SelectorModal';
import GranelModal from './components/GranelModal/GranelModal';
import { localDB } from '../../../../../crud/localDB/localDB';
import { calculateMenuStatus, formatRelativeDateTime } from '../../../Menu/Menu/utils/menu';
import { useStockStatus } from './hooks/useStockStatus';
import './OrderTaker.scss';
import { toast } from 'react-toastify';
import { uuidv4 } from '../../../../../utils/functions';
import { ChevronLeft } from 'react-bootstrap-icons';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FaPaperPlane, FaReceipt, FaClock, FaBan, FaCalendarCheck, FaTableColumns, FaTableList } from 'react-icons/fa6';

interface OrderContext {
  client_id: number | null;
  client_name: string | null;
  pedido_id?: number | null;
  mesa_id: number | null;
  mesa_num: number | null;
  pedido_group_id?: number | null;
  hora_entrega?: string | null;
  priority?: number | null;
  third_party_provider_code: string | null;
  third_party_provider_id: number | null;
  pedido_comment?: string | null;
}

interface OrderTakerProps {
  pedido_type?: 'Delivery' | 'Caja' | 'Restaurant' | 'Autoservicio';
  totalClients?: number | null;
  orderContext?: OrderContext;
  setViewMode?: any;
  setReceipts?: any;
  mesa?: any;
  onPedidoAdded?: () => void;
  disableLayoutToggle?: boolean;
}

interface MenuCategoryNode {
  id: number;
  name: string;
  subcategories: MenuCategoryNode[];
  items: Item[];
  position?: number | null;
  category_type_name?: string;
  category_type_id?: number;
  have_subCategory?: boolean;
}

const OrderTaker: React.FC<OrderTakerProps> = ({
  pedido_type = 'Restaurant',
  totalClients,
  orderContext = {
    pedido_id: null,
    client_id: null,
    client_name: null,
    mesa_id: null,
    mesa_num: null,
    hora_entrega: null,
    priority: null,
    third_party_provider_code: null,
    third_party_provider_id: null,
  },
  setViewMode = (vm: string) => {},
  setReceipts = (r: any) => {},
  mesa = { used: false },
  onPedidoAdded,
  disableLayoutToggle = false,
}) => {
  // State for menus and currently selected menu (by index)
  const [allMenus, setAllMenus] = useState<any[]>([]);
  const [allMenusUnfiltered, setAllMenusUnfiltered] = useState<any[]>([]); // Keep all menus for info display
  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number>(0);

  // Category tree states
  const [rootCategories, setRootCategories] = useState<MenuCategoryNode[]>([]);
  const [currentCategories, setCurrentCategories] = useState<MenuCategoryNode[]>([]);
  const [currentCategoryPath, setCurrentCategoryPath] = useState<MenuCategoryNode[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<MenuCategoryNode | null>(null);

  // Items from the currently selected menu (all items in all categories)
  const [allItems, setAllItems] = useState<Item[]>([]);
  // Items from ALL menus (for cross-menu search)
  const [allMenusItems, setAllMenusItems] = useState<Array<Item & { menu_name: string; menu_id: number }>>([]);
  // Items of the currently selected (leaf) category
  const [categoryItems, setCategoryItems] = useState<Item[]>([]);

  // Cart and modal states
  const [cart, setCart] = useState<CartItemSelection[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [clientIndex, setClientIndex] = useState<number>(0);
  
  // Granel modal states
  const [showGranelModal, setShowGranelModal] = useState<boolean>(false);
  const [granelItem, setGranelItem] = useState<Item | null>(null);
  const [editingGranelCartItem, setEditingGranelCartItem] = useState<CartItemSelection | null>(null);

  // Loading states
  const [loadingEnviar, setLoadingEnviar] = useState(false);
  const [loadingEnviarYCobrar, setLoadingEnviarYCobrar] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(false);

  // Pedidos configuration
  const [pedidosConfig, setPedidosConfig] = useState({
    sortItemsAlphabetically: false,
    sortCategoriesAlphabetically: false,
    alwaysShowSelectorModal: false,
    enableDoubleClickCourse: false
  });

  // Layout mode: 'classic' (vertical) or 'wide' (horizontal split)
  const [layoutMode, setLayoutMode] = useState<'classic' | 'wide'>('classic');
  // Cart collapsed state for classic mode
  const [cartCollapsed, setCartCollapsed] = useState(false);

  // Get local_id from localStorage for stock status
  const localId = (() => {
    try {
      const userContext = localStorage.getItem('user');
      return userContext ? JSON.parse(userContext).local_id : null;
    } catch {
      return null;
    }
  })();

  // Use stock status hook
  const strictStock = useStrictStock(localId);
  const [stockNow, setStockNow] = useState(Date.now);
  useEffect(() => {
    if (!strictStock.enabled) return;
    const timer = window.setInterval(() => setStockNow(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, [strictStock.enabled]);
  const { getItemStockInfo, isLoading: stockLoading, error: stockError, checkedAt, refetch: refreshStock } =
    useStockStatus(localId, strictStock.enabled);

  // Helper: Recursively extract all items from category nodes
  const extractAllItems = (categories: MenuCategoryNode[]): Item[] => {
    if (!categories || !Array.isArray(categories)) {
      return [];
    }
    
    let result: Item[] = [];
    categories.forEach((cat) => {
      // Defensive check for items array
      if (cat?.items && Array.isArray(cat.items)) {
        result.push(...cat.items);
      }
      
      // Defensive check for subcategories
      if (cat?.subcategories && Array.isArray(cat.subcategories) && cat.subcategories.length > 0) {
        result.push(...extractAllItems(cat.subcategories));
      }
    });
    return result;
  };

  // Helper: Extract items from all menus with menu metadata
  const extractItemsFromAllMenus = (menus: any[]): Array<Item & { menu_name: string; menu_id: number }> => {
    const allItems: Array<Item & { menu_name: string; menu_id: number }> = [];
    menus.forEach((menu) => {
      const menuItems = extractAllItems(menu.categories || []);
      menuItems.forEach((item) => {
        allItems.push({
          ...item,
          menu_name: menu.menu_name,
          menu_id: menu.menu_id,
        });
      });
    });
    return allItems;
  };

  /**
   * Sort categories by position.
   * Categories with null/undefined position go to the end, sorted by name.
   * Also recursively sorts subcategories.
   */
  const sortCategoriesByPosition = (categories: MenuCategoryNode[]): MenuCategoryNode[] => {
    return [...categories].sort((a, b) => {
      // Both have positions - sort by position
      if (a.position != null && b.position != null) {
        return a.position - b.position;
      }
      // Only a has position - a comes first
      if (a.position != null && b.position == null) {
        return -1;
      }
      // Only b has position - b comes first
      if (a.position == null && b.position != null) {
        return 1;
      }
      // Neither has position - sort alphabetically by name
      return (a.name || '').localeCompare(b.name || '');
    }).map(cat => ({
      ...cat,
      // Also sort subcategories recursively
      subcategories: cat.subcategories ? sortCategoriesByPosition(cat.subcategories) : []
    }));
  };

    // On mount, load menus from localStorage or API
  useEffect(() => {
    setLoadingMenu(true);
    
    let localMenus: any[] = [];
    try {
      const menusStr = localStorage.getItem('localMenus');
      if (menusStr) {
        localMenus = JSON.parse(menusStr);
        // Validate it's actually an array
        if (!Array.isArray(localMenus)) {
          console.warn('localMenus is not an array, resetting');
          localMenus = [];
          localStorage.removeItem('localMenus');
        }
      }
    } catch (error) {
      console.error('Error parsing localMenus from localStorage:', error);
      localStorage.removeItem('localMenus');
      localMenus = [];
    }

    if (localMenus.length === 0 || orderContext?.third_party_provider_id) {
      // Fetch from API
      let user: any = {};
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          user = JSON.parse(userStr);
        }
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        toast.error('Error de sesión. Por favor, inicie sesión nuevamente.');
        setLoadingMenu(false);
        return;
      }
      
      const localId = user?.local_id || 0;
      if (!localId) {
        toast.error('No se pudo obtener información del local');
        setLoadingMenu(false);
        return;
      }
      
      getAllBuiltMenusByLocalIdV2(localId, pedido_type, false, orderContext?.third_party_provider_id ?? undefined)
        .then((response) => {
          const menus = response?.data;
          if (!menus || !Array.isArray(menus)) {
            throw new Error('Invalid menus data structure');
          }
          
          // Store all menus for info display
          setAllMenusUnfiltered(menus);
          
          // Filter out inactive menus (state === false) AND closed menus (based on schedule)
          const activeMenus = menus.filter(menu => {
            if (!menu || menu.state === false) return false;
            
            // If menu has a schedule, check if it's currently open
            if (menu.schedule) {
              const menuStatus = calculateMenuStatus(menu.schedule);
              return menuStatus.isOpen;
            }
            
            // If no schedule, menu is always available
            return true;
          });
          
          setAllMenus(activeMenus);
          if (activeMenus.length > 0) {
            updateMenuSelection(0, activeMenus);
          }
          
          const allItems = extractItemsFromAllMenus(activeMenus);
          setAllMenusItems(allItems);
          
          try {
            // Only cache generic menus — never overwrite with provider-specific pricing
            if (!orderContext?.third_party_provider_id) {
              localStorage.setItem('localMenus', JSON.stringify(activeMenus));
            }
          } catch (storageError) {
            console.error('Error saving menus to localStorage:', storageError);
          }
          
          setLoadingMenu(false);
        })
        .catch((error) => {
          console.error('Error fetching menus:', error);
          toast.error('Error al cargar menús. Por favor, recargue la página.');
          setLoadingMenu(false);
        });
    } else {
      // Store all menus from localStorage for info display
      setAllMenusUnfiltered(localMenus);
      
      // Filter out inactive menus AND closed menus from localStorage as well
      const activeMenus = localMenus.filter(menu => {
        if (!menu || menu.state === false) return false;
        
        // If menu has a schedule, check if it's currently open
        if (menu.schedule) {
          const menuStatus = calculateMenuStatus(menu.schedule);
          return menuStatus.isOpen;
        }
        
        // If no schedule, menu is always available
        return true;
      });
      
      setAllMenus(activeMenus);
      if (activeMenus.length > 0) {
        updateMenuSelection(0, activeMenus);
      }
      setAllMenusItems(extractItemsFromAllMenus(activeMenus));
      setLoadingMenu(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido_type, orderContext?.third_party_provider_id]);

  // Load pedidos configuration from localDB
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const sortItems = await localDB.get<boolean>('pedidosConfig', 'sortItemsAlphabetically');
        const sortCategories = await localDB.get<boolean>('pedidosConfig', 'sortCategoriesAlphabetically');
        const alwaysShowModal = await localDB.get<boolean>('pedidosConfig', 'alwaysShowSelectorModal');
        const enableDoubleClick = await localDB.get<boolean>('pedidosConfig', 'enableDoubleClickCourse');
        
        setPedidosConfig({
          sortItemsAlphabetically: sortItems ?? false,
          sortCategoriesAlphabetically: sortCategories ?? false,
          alwaysShowSelectorModal: alwaysShowModal ?? false,
          enableDoubleClickCourse: enableDoubleClick ?? false
        });

        const savedLayoutMode = await localDB.get<string>('pedidosConfig', 'layoutMode');
        if (!disableLayoutToggle && (savedLayoutMode === 'classic' || savedLayoutMode === 'wide')) {
          setLayoutMode(savedLayoutMode);
        } else if (disableLayoutToggle) {
          setLayoutMode('classic');
        }
      } catch (error) {
        console.error('Error loading pedidos config:', error);
      }
    };
    loadConfig();
  }, [disableLayoutToggle]);

  // Clear cart when orderContext changes (switching tables/groups/pedido types)
  useEffect(() => {
    // Create a unique identifier for the current order context
    const contextId = orderContext 
      ? `${orderContext.mesa_id || ''}_${orderContext.pedido_group_id || ''}_${orderContext.client_id || ''}_${pedido_type}`
      : null;
    
    // Store previous context ID
    const prevContextIdRef = { current: contextId };
    
    return () => {
      // If context changed (not just initial mount), clear the cart
      if (prevContextIdRef.current !== contextId && prevContextIdRef.current !== null) {
        setCart([]);
      }
    };
  }, [orderContext, pedido_type]);

  // Additional effect to clear cart when switching between contexts
  useEffect(() => {
    // Clear cart when switching mesas, groups, or pedido types
    setCart([]);
  }, [orderContext?.mesa_id, orderContext?.pedido_group_id, orderContext?.client_id, pedido_type]);

    // Re-apply sorting when config changes
  useEffect(() => {
    if (rootCategories.length > 0) {
      // First sort by position (default behavior)
      let cats = sortCategoriesByPosition(rootCategories);
      // Override with alphabetical sort if enabled
      if (pedidosConfig.sortCategoriesAlphabetically) {
        cats = [...cats].sort((a: MenuCategoryNode, b: MenuCategoryNode) => a.name.localeCompare(b.name));
      }
      setCurrentCategories(cats);
      
      // If there's a selected category, re-sort its items too
      if (selectedCategory) {
        let items = [...selectedCategory.items];
        if (pedidosConfig.sortItemsAlphabetically) {/* Lines 186-187 omitted */}
        setCategoryItems(items);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidosConfig.sortCategoriesAlphabetically, pedidosConfig.sortItemsAlphabetically]);

  // When a menu is selected, update category tree and allItems accordingly.
  const updateMenuSelection = (index: number, menus: any[]) => {
    // Validate inputs
    if (!menus || !Array.isArray(menus) || index < 0 || index >= menus.length) {
      console.error('Invalid menu selection:', { index, menusLength: menus?.length });
      // Reset to safe state
      setRootCategories([]);
      setCurrentCategories([]);
      setCurrentCategoryPath([]);
      setSelectedCategory(null);
      setCategoryItems([]);
      setAllItems([]);
      return;
    }

    setSelectedMenuIndex(index);
    const selectedMenu = menus[index];
    
    // Validate menu structure
    if (!selectedMenu || !selectedMenu.categories || !Array.isArray(selectedMenu.categories)) {
      console.error('Invalid menu structure:', selectedMenu);
      toast.error('Error: Menú con estructura inválida');
      setRootCategories([]);
      setCurrentCategories([]);
      setAllItems([]);
      return;
    }
    
    let categories = selectedMenu.categories;
    
    // First sort by position (default behavior)
    categories = sortCategoriesByPosition(categories);
    
    // Override with alphabetical sort if enabled
    if (pedidosConfig.sortCategoriesAlphabetically) {
      categories = [...categories].sort((a: MenuCategoryNode, b: MenuCategoryNode) => 
        (a?.name || '').localeCompare(b?.name || '')
      );
    }
    
    setRootCategories(categories);
    setCurrentCategories(categories);
    setCurrentCategoryPath([]);
    setSelectedCategory(null);
    setCategoryItems([]);
    setAllItems(extractAllItems(categories));
  };

  const handleMenuChange = (index: number) => {
    updateMenuSelection(index, allMenus);
  };

  // When a category is clicked: if it has subcategories, navigate deeper; otherwise, select it as leaf.
  const handleCategorySelect = (category: MenuCategoryNode) => {
    if (category.subcategories && category.subcategories.length > 0) {
      setCurrentCategoryPath((prevPath) => [...prevPath, category]);
      
      // First sort by position (default behavior)
      let subcats = sortCategoriesByPosition(category.subcategories);
      
      // Override with alphabetical sort if enabled
      if (pedidosConfig.sortCategoriesAlphabetically) {
        subcats = [...subcats].sort((a: MenuCategoryNode, b: MenuCategoryNode) => a.name.localeCompare(b.name));
      }
      
      setCurrentCategories(subcats);
      setSelectedCategory(null);
      setCategoryItems([]);
    } else {
      setSelectedCategory(category);
      
      let items = category.items;
      if (pedidosConfig.sortItemsAlphabetically) {
        items = [...items].sort((a: Item, b: Item) => a.item_name.localeCompare(b.item_name));
      }
      
      setCategoryItems(items);
    }
  };

  // “Go back” to parent category
  const handleGoBack = () => {
    setCurrentCategoryPath((prevPath) => {
      const newPath = [...prevPath];
      newPath.pop();
      if (newPath.length === 0) {
        // First sort by position (default behavior)
        let cats = sortCategoriesByPosition(rootCategories);
        // Override with alphabetical sort if enabled
        if (pedidosConfig.sortCategoriesAlphabetically) {
          cats = [...cats].sort((a: MenuCategoryNode, b: MenuCategoryNode) => a.name.localeCompare(b.name));
        }
        setCurrentCategories(cats);
        setSelectedCategory(null);
        setCategoryItems([]);
      } else {
        const parent = newPath[newPath.length - 1];
        // First sort by position (default behavior)
        let subcats = sortCategoriesByPosition(parent.subcategories);
        // Override with alphabetical sort if enabled
        if (pedidosConfig.sortCategoriesAlphabetically) {
          subcats = [...subcats].sort((a: MenuCategoryNode, b: MenuCategoryNode) => a.name.localeCompare(b.name));
        }
        setCurrentCategories(subcats);
        setSelectedCategory(null);
        setCategoryItems([]);
      }
      return newPath;
    });
  };

  // Check if an item has any optional additions
  const hasOptionalAdditions = (item: Item): boolean => {
    if (!item) {
      return false;
    }
    
    const hasOptionalSg = Array.isArray(item.selection_groups) && item.selection_groups.some(
      (sg) => sg && sg.min_selections === 0 && sg.min_variance === 0
    );
    
    const hasOptionalModifiers = Array.isArray(item.modifiers) && item.modifiers.some(
      (m) => m && m.is_optional
    );
    
    return hasOptionalSg || hasOptionalModifiers;
  };

  // When an item is selected from the global search:
  // - If it's granel, open GranelModal
  // - If has mandatory additions, open SelectorModal  
  // - Otherwise, add directly to the cart.
  const handleGlobalItemSelect = (item: Item) => {
    // Validate item structure
    if (!item || !item.item_id) {
      console.error('Invalid item selected:', item);
      toast.error('Error: Item inválido');
      return;
    }

    // Strip menu metadata if present (when coming from allMenusItems)
    const cleanItem: Item = {
      item_id: item.item_id,
      item_name: item.item_name || 'Sin nombre',
      label: item.label || '',
      price: item.price || 0,
      selection_groups: Array.isArray(item.selection_groups) ? item.selection_groups : [],
      modifiers: Array.isArray(item.modifiers) ? item.modifiers : [],
      is_granel: item.is_granel || false,
      description: item.description,
      main_image: item.main_image,
      image_list: item.image_list,
      isCombo: item.isCombo,
      image_url: item.image_url,
      state: item.state,
      gluten: item.gluten,
      vegano: item.vegano,
      sodio: item.sodio,
      lactosa: item.lactosa,
      azucar: item.azucar,
      vegetariano: item.vegetariano,
      alcohol: item.alcohol,
      picante: item.picante,
      units: item.units,
      is_online: item.is_online,
      price_online: item.price_online,
      is_desk: item.is_desk,
      price_desk: item.price_desk,
      category_id: item.category_id,
      local_id: item.local_id,
      unidad_medida_id: item.unidad_medida_id,
      parent_item_id: item.parent_item_id,
      is_delivery: item.is_delivery,
      price_delivery: item.price_delivery,
    };
    
    if (cleanItem.is_granel) {
      setGranelItem(cleanItem);
      setEditingGranelCartItem(null);
      setShowGranelModal(true);
      return;
    }

    const hasOptional = hasOptionalAdditions(cleanItem);
    
    // Auto-select single-item mandatory selection groups with defensive checks
    const autoSelectedGroups = cleanItem.selection_groups
      .filter(sg => {
        // Validate selection group structure
        if (!sg || typeof sg.min_selections !== 'number') return false;
        if (!Array.isArray(sg.items)) return false;
        return sg.min_selections > 0 && sg.items.length === 1;
      })
      .map(sg => {
        const firstItem = sg.items[0];
        return {
          selection_group_id: sg.selection_group_id,
          selection_group_name: sg.selection_group_name || '',
          selected_items: [{
            item_id: firstItem.item_id,
            item_name: firstItem.item_name || '',
            quantity: sg.min_selections,
            // Use pedidoType-specific price modifier
            price_modifier: getSelectionItemPriceModifier(firstItem, pedido_type)
          }]
        };
      });

    // Calculate total with auto-selected items, using pedidoType-specific base price
    let totalPrice = getItemPrice(cleanItem, pedido_type);
    autoSelectedGroups.forEach(sg => {
      sg.selected_items.forEach(sgi => {
        totalPrice += (sgi.price_modifier || 0) * (sgi.quantity || 0);
      });
    });

    // Check if there are mandatory selections that need user input (more than one option)
    const hasMandatoryNeedingInput = cleanItem.selection_groups.some(
      sg => sg && sg.min_selections > 0 && Array.isArray(sg.items) && sg.items.length > 1
    );
    const hasMandatoryModifiers = cleanItem.modifiers.some(
      m => m && !m.is_optional && Array.isArray(m.options) && m.options.length > 0
    );
    
    // Determine if we need to show the modal
    const needsModal = hasMandatoryNeedingInput || 
                      hasMandatoryModifiers ||
                      (hasOptional && pedidosConfig.alwaysShowSelectorModal);

    if (needsModal) {
      setSelectedItemId(cleanItem.item_id);
      setEditingIndex(null);
      setShowModal(true);
    } else {
      // Add directly to cart with auto-selected groups
      try {
        const cartItem: CartItemSelection = {
          selection_id: uuidv4(),
          item_id: cleanItem.item_id,
          item_name: cleanItem.item_name,
          quantity: 1,
          base_price: getItemPrice(cleanItem, pedido_type), // Use pedidoType-specific price
          selection_groups: autoSelectedGroups,
          modifiers: [],
          total_price: totalPrice,
          comment: '',
          clientIndex: clientIndex,
        };
        setCart((prev) => [...prev, cartItem]);
      } catch (error) {
        console.error('Error adding item to cart:', error);
        toast.error('Error al agregar item al carrito');
      }
    }
  };

  // --- Cart and order functions (as before) ---
  const loadCartItemForEditing = (index: number) => {
    const cartItem = cart[index];
    
    if (cartItem.is_granel) {
      // Edit granel item - search in allMenusItems first, then fallback to allItems
      const originalItem = (allMenusItems.length > 0 
        ? allMenusItems.find(item => item.item_id === cartItem.item_id)
        : allItems.find(item => item.item_id === cartItem.item_id));
      if (originalItem) {
        setGranelItem(originalItem);
        setEditingGranelCartItem(cartItem);
        setShowGranelModal(true);
      }
    } else {
      // Edit regular item
      setSelectedItemId(cartItem.item_id);
      setEditingIndex(index);
      setShowModal(true);
    }
  };

  // Handle adding/updating granel items in cart
  const handleGranelAddToCart = (cartItem: CartItemSelection) => {
    if (editingGranelCartItem) {
      // Update existing granel item
      setCart((prev) => prev.map((item, index) => 
        item.selection_id === editingGranelCartItem.selection_id ? cartItem : item
      ));
    } else {
      // Add new granel item
      setCart((prev) => [...prev, cartItem]);
    }
    
    // Reset granel modal state
    setGranelItem(null);
    setEditingGranelCartItem(null);
  };

  const handleCartQuantityChange = (index: number, newQuantity: number) => {
    setCart((prev) => {
      // Validate index
      if (index < 0 || index >= prev.length) {
        console.error('Invalid cart index:', index);
        return prev;
      }

      const newCart = [...prev];
      
      if (newQuantity <= 0) {
        newCart.splice(index, 1);
        return newCart;
      }
      
      const ci = newCart[index];
      
      // Validate cart item structure
      if (!ci || !ci.item_id) {
        console.error('Invalid cart item at index:', index);
        return prev;
      }
      
      // Search in allMenusItems first, then fallback to allItems
      const item = (allMenusItems.length > 0
        ? allMenusItems.find((i) => i && i.item_id === ci.item_id)
        : allItems.find((i) => i && i.item_id === ci.item_id));
        
      if (!item) {
        console.error('Item not found for cart item:', ci.item_id);
        toast.error('Error: No se encontró el item en el menú');
        return prev;
      }
  
      // Calculate the multiplier for selection group items
      const oldQuantity = ci.quantity || 1;
      const quantityMultiplier = newQuantity / oldQuantity;
  
      // Update selection group item quantities proportionally with defensive checks
      if (Array.isArray(ci.selection_groups)) {
        ci.selection_groups.forEach((sg) => {
          if (sg && Array.isArray(sg.selected_items)) {
            sg.selected_items.forEach((sgi) => {
              if (sgi && typeof sgi.quantity === 'number') {
                sgi.quantity = Math.round(sgi.quantity * quantityMultiplier);
              }
            });
          }
        });
      }
  
      let total = ci.total_price && ci.quantity > 0
        ? ci.total_price / ci.quantity // Mantiene el precio original si ya existía
        : (item.price || 0); // Usa el precio base si no hay total_price previo
  
      // Solo calcular agregados si no se tiene un precio fijo
      if (!ci.total_price || ci.quantity === 0) {
        // Defensive iteration over selection groups
        if (Array.isArray(ci.selection_groups) && Array.isArray(item.selection_groups)) {
          ci.selection_groups.forEach((sg) => {
            if (!sg) return;
            
            const groupData = item.selection_groups.find(
              (x) => x && x.selection_group_id === sg.selection_group_id
            );
            
            if (groupData && Array.isArray(groupData.items) && Array.isArray(sg.selected_items)) {
              sg.selected_items.forEach((sgi) => {
                if (!sgi) return;
                
                const sgItem = groupData.items.find((x) => x && x.item_id === sgi.item_id);
                if (sgItem && typeof sgi.quantity === 'number' && sgi.quantity > 0) {
                  total += getSelectionItemPriceModifier(sgItem, pedido_type) * sgi.quantity;
                }
              });
            }
          });
        }
  
        // Defensive iteration over modifiers
        if (Array.isArray(ci.modifiers) && Array.isArray(item.modifiers)) {
          ci.modifiers.forEach((m) => {
            if (!m) return;
            
            const modData = item.modifiers.find((x) => x && x.modifier_id === m.modifier_id);
            if (modData && Array.isArray(modData.options)) {
              const opt = modData.options.find((o) => o && o.option_id === m.option_id);
              if (opt && typeof opt.price_modifier === 'number') {
                total += getModifierOptionPriceModifier(opt, pedido_type);
              }
            }
          });
        }
      }
  
      total = total * newQuantity;
      ci.quantity = newQuantity;
      ci.total_price = total;
      return newCart;
    });
  };

  const handleDeleteCartItem = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCartItemCommentChange = (index: number, comment: string) => {
    setCart((prev) => {
      const newCart = [...prev];
      if (newCart[index]) {
        newCart[index] = {
          ...newCart[index],
          comment: comment.trim() || undefined
        };
      }
      return newCart;
    });
  };

  const handleCourseTagChange = (index: number, tag: 'ENTRADA' | 'FONDO' | undefined) => {
    setCart((prev) => {
      const newCart = [...prev];
      if (newCart[index]) {
        newCart[index] = {
          ...newCart[index],
          course_tag: tag
        };
      }
      return newCart;
    });
  };

  const handleCartClientIndexChange = (index: number, clientIndex: number) => {
    setCart((prev) => {
      const newCart = [...prev];
      if (newCart[index]) {
        newCart[index] = {
          ...newCart[index],
          clientIndex: clientIndex
        };
      }
      return newCart;
    });
  };

  const handleToggleLayout = async () => {
    const newMode = layoutMode === 'classic' ? 'wide' : 'classic';
    setLayoutMode(newMode);
    try {
      await localDB.set('pedidosConfig', 'layoutMode', newMode);
    } catch (error) {
      console.error('Error saving layout mode:', error);
    }
  };

  const handleEnviarOrden = () => {
    // Validate cart before processing
    if (!cart || cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }
    
    setLoadingEnviar(true);
    
    // Safe user parsing with try-catch
    let user: any = {};
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('Usuario no encontrado');
      }
      user = JSON.parse(userStr);
      
      // Validate required user fields
      if (!user.user_id || !user.local_id) {
        throw new Error('Datos de usuario incompletos');
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      toast.error('Error: Sesión de usuario inválida. Por favor, inicie sesión nuevamente.');
      setLoadingEnviar(false);
      return;
    }
    
    const userContext = {
      user_id: user.user_id,
      name: user.name || 'Usuario',
      local_id: user.local_id,
    };
    
    // Use pedido_type_id = 5 for virtual tables, otherwise use the mapped type
    const pedidoTypeId = mesa && mesa.is_virtual ? 5 : (pedidoTypeMap[pedido_type] || 1);
    
    // Validate orderContext
    if (!orderContext) {
      toast.error('Error: Contexto de pedido no válido');
      setLoadingEnviar(false);
      return;
    }
    
    const payload = {
      total_clients: totalClients || 1,
      pedido_id: orderContext.pedido_id || undefined,
      mesa_id: orderContext.mesa_id,
      mesa_num: orderContext.mesa_num,
      pedido_group_id: orderContext.pedido_group_id,
      pedido_type_id: pedidoTypeId,
      client_id: orderContext.client_id,
      client_name: orderContext.client_name,
      hora_entrega: orderContext.hora_entrega,
      priority: orderContext.priority,
      local_id: userContext.local_id,
      user_id: userContext.user_id,
      user_name: userContext.name,
      selections: cart,
      third_party_provider_code: orderContext.third_party_provider_code,
      third_party_provider_id: orderContext.third_party_provider_id,
      comment: orderContext.pedido_comment || undefined,
    };

    strictStock.submit(payload)
      .then((response) => {
        // OPTIMISTIC: Only reject on explicit 4xx/5xx errors, otherwise assume success
        // Check for explicit error status codes (4xx, 5xx)
        if (response?.status && (response.status >= 400)) {
          throw new Error(`Error del servidor (código ${response.status})`);
        }
        
        // Log response for debugging (only in development)
        if (process.env.NODE_ENV === 'development') {
          console.log('CreatePedidoV2 Response:', response);
        }
        
        // OPTIMISTIC SUCCESS: Even if response is malformed, treat as success
        // Just protect against unsafe property access with try-catch
        setCart([]);
        toast.success('Pedido creado con éxito');
        
        // Safe callback execution - don't let callback errors break the flow
        try {
          if (onPedidoAdded && typeof onPedidoAdded === 'function') {
            onPedidoAdded();
          }
        } catch (callbackError) {
          console.error('Error in onPedidoAdded callback:', callbackError);
        }
        
        // Safe mesa state update
        try {
          if (mesa && typeof mesa === 'object' && mesa.used === false) {
            mesa.used = true;
          }
        } catch (mesaError) {
          console.error('Error updating mesa state:', mesaError);
        }
        
        // Safe view mode change
        try {
          if (setViewMode && typeof setViewMode === 'function') {
            setViewMode('mesa');
          }
        } catch (viewError) {
          console.error('Error changing view mode:', viewError);
        }
      })
      .catch((error: any) => {
        if (error?.strictStock) {
          if (error.cancelled) return;
          toast.error(stockMessages.unknown);
          return;
        }
        // Only show error to user if it's an explicit HTTP error
        console.error('Error creating pedido:', error);
        
        // Check if this is an HTTP error (4xx/5xx)
        const isHttpError = error?.response?.status && error.response.status >= 400;
        
        if (isHttpError) {
          let errorMessage = 'Error al crear el pedido';
          
          try {
            if (error?.response?.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error?.response?.data?.error) {
              errorMessage = error.response.data.error;
            } else if (error?.message) {
              errorMessage = error.message;
            }
          } catch (parseError) {
            console.error('Error parsing error message:', parseError);
          }
          
          toast.error('Error creando el pedido: ' + errorMessage);
        } else {
          // Network errors, timeouts, etc. - log but don't show to user
          console.warn('Non-HTTP error in createPedidoV2 (optimistically ignored):', error);
        }
      })
      .finally(() => {
        setLoadingEnviar(false);
      });
  };

  const handleEnviarYCobrar = () => {
    // Validate cart before processing
    if (!cart || cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    setLoadingEnviarYCobrar(true);
    
    // Safe user parsing with try-catch
    let user: any = {};
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('Usuario no encontrado');
      }
      user = JSON.parse(userStr);
      
      // Validate required user fields
      if (!user.user_id || !user.local_id) {
        throw new Error('Datos de usuario incompletos');
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      toast.error('Error: Sesión de usuario inválida. Por favor, inicie sesión nuevamente.');
      setLoadingEnviarYCobrar(false);
      return;
    }
    
    const userContext = {
      user_id: user.user_id,
      name: user.name || 'Usuario',
      local_id: user.local_id,
    };
    
    // Use pedido_type_id = 5 for virtual tables (Mesa Virtual)
    const pedidoTypeId = 5;
    
    // Validate orderContext
    if (!orderContext) {
      toast.error('Error: Contexto de pedido no válido');
      setLoadingEnviarYCobrar(false);
      return;
    }
    
    const payload = {
      total_clients: totalClients || 1,
      pedido_id: orderContext.pedido_id || undefined,
      mesa_id: orderContext.mesa_id,
      mesa_num: orderContext.mesa_num,
      pedido_group_id: orderContext.pedido_group_id,
      pedido_type_id: pedidoTypeId,
      client_id: orderContext.client_id,
      client_name: orderContext.client_name,
      hora_entrega: orderContext.hora_entrega,
      priority: orderContext.priority,
      local_id: userContext.local_id,
      user_id: userContext.user_id,
      user_name: userContext.name,
      selections: cart,
      third_party_provider_code: orderContext.third_party_provider_code,
      third_party_provider_id: orderContext.third_party_provider_id,
      comment: orderContext.pedido_comment || undefined,
    };

    strictStock.submit(payload)
      .then((response) => {
        // OPTIMISTIC: Only reject on explicit 4xx/5xx errors
        if (response?.status && response.status >= 400) {
          throw new Error(`Error del servidor (código ${response.status})`);
        }
        
        // Log response for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('CreatePedidoV2 Response (Enviar y Cobrar):', response);
        }
        
        // OPTIMISTIC SUCCESS: Assume order was created successfully
        setCart([]);
        
        // Safe mesa state update
        try {
          if (mesa && typeof mesa === 'object' && mesa.used === false) {
            mesa.used = true;
          }
        } catch (mesaError) {
          console.error('Error updating mesa state:', mesaError);
        }

        // Validate required fields for receipt generation
        if (!orderContext.mesa_id) {
          toast.error('Error: No se puede generar cuenta sin mesa');
          setLoadingEnviarYCobrar(false);
          return;
        }

        if (!userContext.user_id) {
          toast.error('Error: Usuario no válido para generar cuenta');
          setLoadingEnviarYCobrar(false);
          return;
        }

        // Don't switch view yet - stay in loading until receipt is ready
        const strictAccepted = response.data?.strict_stock === true;
        createPaymentReceiptByMesaIdV2(orderContext.mesa_id, 'single', userContext.user_id, 1)
          .then((receiptResponse: any) => {
            if (strictAccepted) {
              if (!receiptResponse || receiptResponse.status < 200 || receiptResponse.status >= 300
                  || typeof receiptResponse.status !== 'number' || !receiptResponse.data) {
                throw new Error(stockMessages.receiptUnknown);
              }
              onPedidoAdded?.();
              setViewMode?.('payment');
              setLoadingEnviarYCobrar(false);
              return;
            }
            // OPTIMISTIC: Only reject on explicit 4xx/5xx errors
            if (receiptResponse?.status && receiptResponse.status >= 400) {
              throw new Error(`Error al generar cuenta (código ${receiptResponse.status})`);
            }
            
            // Log receipt response for debugging
            if (process.env.NODE_ENV === 'development') {
              console.log('CreatePaymentReceipt Response:', receiptResponse);
            }
            
            // OPTIMISTIC SUCCESS: Assume receipt was created
            // FORCE switch to payment view IMMEDIATELY
            try {
              if (setViewMode && typeof setViewMode === 'function') {
                setViewMode('payment');
              }
            } catch (viewError) {
              console.error('Error switching to payment view:', viewError);
            }
            
            // Call onPedidoAdded AFTER we've already set payment view
            try {
              if (onPedidoAdded && typeof onPedidoAdded === 'function') {
                onPedidoAdded();
              }
            } catch (callbackError) {
              console.error('Error in onPedidoAdded callback:', callbackError);
            }
            
            // Force payment view again after parent refresh, just to be sure
            setTimeout(() => {
              try {
                if (setViewMode && typeof setViewMode === 'function') {
                  setViewMode('payment');
                }
              } catch (error) {
                console.error('Error in setTimeout view change:', error);
              }
            }, 50);
            
            setTimeout(() => {
              try {
                if (setViewMode && typeof setViewMode === 'function') {
                  setViewMode('payment');
                }
              } catch (error) {
                console.error('Error in setTimeout view change:', error);
              }
            }, 200);
            
            toast.success('Pedido enviado y cuenta generada');
            setLoadingEnviarYCobrar(false);
          })
          .catch((error: any) => {
            if (strictAccepted) {
              toast.error(stockMessages.receiptUnknown);
              setLoadingEnviarYCobrar(false);
              return;
            }
            // Only show error if it's an explicit HTTP error
            console.error('Error creating receipt:', error);
            
            const isHttpError = error?.response?.status && error.response.status >= 400;
            
            if (isHttpError) {
              let errorMessage = 'Error al generar cuenta';
              
              try {
                if (error?.response?.data?.message) {
                  errorMessage = error.response.data.message;
                } else if (error?.response?.data?.error) {
                  errorMessage = error.response.data.error;
                } else if (error?.message) {
                  errorMessage = error.message;
                }
              } catch (parseError) {
                console.error('Error parsing receipt error:', parseError);
              }
              
              toast.error('Error generando la cuenta: ' + errorMessage);
            } else {
              // Network errors - log but don't show to user
              console.warn('Non-HTTP error in createPaymentReceipt (optimistically ignored):', error);
            }
            
            setLoadingEnviarYCobrar(false);
          });
      })
      .catch((error: any) => {
        // Only show error if it's an explicit HTTP error
        console.error('Error creating pedido:', error);
        if (error?.strictStock) {
          if (error.cancelled) return;
          toast.error(stockMessages.unknown);
          setLoadingEnviarYCobrar(false);
          return;
        }
        
        const isHttpError = error?.response?.status && error.response.status >= 400;
        
        if (isHttpError) {
          let errorMessage = 'Error al crear el pedido';
          
          try {
            if (error?.response?.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error?.response?.data?.error) {
              errorMessage = error.response.data.error;
            } else if (error?.message) {
              errorMessage = error.message;
            }
          } catch (parseError) {
            console.error('Error parsing error message:', parseError);
          }
          
          toast.error('Error creando el pedido: ' + errorMessage);
        } else {
          // Network errors - log but don't show to user
          console.warn('Non-HTTP error in createPedidoV2 (optimistically ignored):', error);
        }
        
        setLoadingEnviarYCobrar(false);
      });
  };

  if (loadingMenu) {
    return <MiniLoadingSpinner />;
  }

  // Check if all menus are closed/inactive and show detailed information
  if (allMenus.length === 0) {
    // Analyze all menus to categorize them
    const inactiveMenus: any[] = [];
    const closedMenus: Array<{ menu: any; nextOpening: Date | null }> = [];

    allMenusUnfiltered.forEach(menu => {
      if (!menu) return;
      
      // Check if menu is inactive (state === false)
      if (menu.state === false) {
        inactiveMenus.push(menu);
      }
      // Check if menu is closed due to schedule
      else if (menu.schedule) {
        const menuStatus = calculateMenuStatus(menu.schedule);
        if (!menuStatus.isOpen) {
          closedMenus.push({ menu, nextOpening: menuStatus.nextOpening });
        }
      }
    });

    // Sort closed menus by next opening time (earliest first)
    closedMenus.sort((a, b) => {
      if (!a.nextOpening) return 1;
      if (!b.nextOpening) return -1;
      return a.nextOpening.getTime() - b.nextOpening.getTime();
    });

    return (
      <div className="order-taker-container">
        <div className="empty-menus-state">
          <div className="empty-menus-icon">
            <FaClock />
          </div>
          
          <h3 className="empty-menus-title">No hay menús disponibles</h3>
          
          <div className="empty-menus-details">
            {closedMenus.length > 0 && (
              <div className="menu-status-section">
                <h4 className="section-title">
                  <FaClock className="status-icon" />
                  Menús Cerrados
                </h4>
                <div className="menu-list">
                  {closedMenus.map(({ menu, nextOpening }) => (
                    <div key={menu.menu_id} className="menu-status-item">
                      <div className="menu-name">
                        {menu.menu_name}
                      </div>
                      <div className="menu-info">
                        <FaCalendarCheck className="info-icon" />
                        {nextOpening ? (
                          <>Abre {formatRelativeDateTime(nextOpening)}</>
                        ) : (
                          <span className="no-schedule">Sin próxima apertura programada</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {inactiveMenus.length > 0 && (
              <div className="menu-status-section">
                <h4 className="section-title">
                  <FaBan className="status-icon" />
                  Menús Inactivos
                </h4>
                <div className="menu-list">
                  {inactiveMenus.map(menu => (
                    <div key={menu.menu_id} className="menu-status-item">
                      <div className="menu-name">
                        {menu.menu_name}
                      </div>
                      <div className="menu-info inactive">
                        <FaBan className="info-icon" />
                        Este menú está desactivado
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <p className="empty-menus-footer">
            {closedMenus.length > 0 && inactiveMenus.length === 0 && 
              "Los menús se activarán automáticamente según sus horarios configurados."
            }
            {inactiveMenus.length > 0 && closedMenus.length === 0 &&
              "Contacte al administrador para activar estos menús."
            }
            {closedMenus.length > 0 && inactiveMenus.length > 0 &&
              "Algunos menús están cerrados y otros desactivados."
            }
          </p>
        </div>
      </div>
    );
  }

  const cartSummaryTotal = cart.reduce((sum, ci) => sum + ci.total_price, 0);

  const renderHeader = () => (
    <div className="order-taker__header">
      {currentCategoryPath.length > 0 && (
        <button className="volver-button" onClick={handleGoBack}>
          <ChevronLeft /> Categorias
        </button>
      )}
      {currentCategories.length > 0 && (
        <div className="order-taker__categories-container">
          {currentCategories.map((category: MenuCategoryNode) => {
            const isSelected = selectedCategory?.id === category.id;
            return (
              <div
                key={category.id}
                onClick={() => handleCategorySelect(category)}
                className={`order-taker__category-item ${isSelected ? 'order-taker__category-item--selected' : ''}`}
                title={category.name.length > 20 ? category.name : undefined}
              >
                {category.name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderMain = () => (
    <div className="order-taker__main">
      {selectedCategory ? (
        <NewOrderItemSelector
          selectedCategory={{
            id: selectedCategory.id,
            name: selectedCategory.name,
            items: categoryItems,
          }}
          pedidoType={pedido_type}
          items={categoryItems}
          onItemSelect={handleGlobalItemSelect}
          getItemStockInfo={getItemStockInfo}
        />
      ) : (
        <div className="empty-category-state">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 3h18v18H3z" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 9h18M9 21V9" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="empty-category-title">Selecciona una categoría</div>
          <div className="empty-category-subtitle">Elige una categoría del menú para ver sus items disponibles</div>
        </div>
      )}
    </div>
  );

  const renderFooterButtons = () => (
    <>
      {pedido_type === 'Restaurant' && (
        <div className="footer-buttons-container">
          <Button 
            className="enviar-orden-button" 
            onClick={handleEnviarOrden}
            variant="none"
            disabled={cart.length === 0 || loadingEnviar || loadingEnviarYCobrar}
          >
            {loadingEnviar ? (
              <>
                <Spinner animation="border" size="sm" className="me-3" />
                Enviando...
              </>
            ) : (
              <>
                Enviar <FaPaperPlane/>
              </>
            )}
          </Button>
          
          {/* Show "Enviar y Cobrar" button only for virtual tables */}
          {mesa && mesa.is_virtual && (
            <Button 
              className="enviar-cobrar-button" 
              onClick={handleEnviarYCobrar}
              variant="none"
              disabled={cart.length === 0 || loadingEnviar || loadingEnviarYCobrar}
            >
              {loadingEnviarYCobrar ? (
                <>
                  <Spinner animation="border" size="sm" className="me-3" />
                  Procesando...
                </>
              ) : (
                <>
                  Enviar y Cobrar <FaReceipt/>
                </>
              )}
            </Button>
          )}
        </div>
      )}
      {pedido_type === 'Delivery' && (
        <div className="footer-buttons-container">
          <Button 
            className="enviar-orden-button" 
            onClick={handleEnviarOrden}
            variant="none"
            disabled={cart.length === 0 || loadingEnviar}
          >
            {loadingEnviar ? (
              <>
                <Spinner animation="border" size="sm" className="me-3" />
                Enviando...
              </>
            ) : (
              <>
                Enviar <FaPaperPlane/>
              </>
            )}
          </Button>
        </div>
      )}
      {pedido_type === 'Caja' && (
        <div className="footer-buttons-container">
          <Button 
            className="enviar-orden-button" 
            onClick={handleEnviarOrden}
            variant="none"
            disabled={cart.length === 0 || loadingEnviar}
          >
            {loadingEnviar ? (
              <>
                <Spinner animation="border" size="sm" className="me-3" />
                Cobrando...
              </>
            ) : (
              'Cobrar'
            )}
          </Button>
        </div>
      )}
    </>
  );

  const renderModals = () => (
    <>
      {selectedItemId && (
        <SelectorModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setSelectedItemId(null);
            setEditingIndex(null);
          }}
          cart={cart}
          setCart={setCart}
          categoryItems={allMenusItems.length > 0 ? allMenusItems : allItems}
          selectedItemId={selectedItemId}
          setSelectedItemId={setSelectedItemId}
          editingIndex={editingIndex}
          setEditingIndex={setEditingIndex}
          clientIndex={clientIndex}
          setClientIndex={setClientIndex}
          pedidoType={pedido_type}
          getItemStockInfo={getItemStockInfo}
        />
      )}
      
      <GranelModal
        show={showGranelModal}
        onHide={() => {
          setShowGranelModal(false);
          setGranelItem(null);
          setEditingGranelCartItem(null);
        }}
        item={granelItem}
        editingCartItem={editingGranelCartItem}
        onAddToCart={handleGranelAddToCart}
        clientIndex={clientIndex}
      />
    </>
  );

  return (
    <div className={`order-taker-container ${layoutMode === 'wide' ? 'order-taker-container--wide' : ''}`}>
      {strictStock.refusal && (
        <Alert variant="danger" role="alert">
          {strictStock.refusal.message}
          {strictStock.refusal.details.map((detail, index) => (
            <p key={`${detail.ingredient_id}-${index}`}>
              {stockMessages.selections}: {detail.selection_ids.map(id =>
                cart.find(selection => selection.selection_id === id)?.item_name || id).join(', ')}.{' '}
              {stockMessages.ingredient}: {detail.ingredient_id}.{' '}
              {stockMessages.missing}: {detail.missing}. {stockMessages.unit}: {detail.unit_id}.
            </p>
          ))}
        </Alert>
      )}
      {strictStock.enabled && (
        <Alert variant={stockError ? 'warning' : 'info'} role="status">
          {stockLoading ? stockMessages.stockLoading : stockError || !checkedAt
            ? stockMessages.stockError : stockNow - checkedAt > 30000
              ? stockMessages.stockStale : stockMessages.stockFresh}
          <Button variant="link" onClick={refreshStock} disabled={stockLoading}>{stockMessages.refresh}</Button>
        </Alert>
      )}
      {layoutMode === 'wide' ? (
        <>
          <div className="order-taker__left-column">
            <div className="order-taker__top-bar">
              <GlobalSearchBar
                items={allItems}
                allMenusItems={allMenusItems}
                menus={allMenus}
                selectedMenuIndex={selectedMenuIndex}
                onMenuChange={handleMenuChange}
                onItemSelect={handleGlobalItemSelect}
                pedidoType={pedido_type}
                getItemStockInfo={getItemStockInfo}
                autoFocus={false}
              />
              {!disableLayoutToggle && (
                <button className="layout-toggle-button" onClick={handleToggleLayout} title="Cambiar a vista clásica" aria-label="Toggle layout mode">
                  <FaTableList />
                </button>
              )}
            </div>
            {renderHeader()}
            {renderMain()}
          </div>
          <div className="order-taker__right-column">
            <div className="order-taker__right-column-content">
              <Cart
                cart={cart}
                pedidoType={pedido_type}
                allItems={allMenusItems.length > 0 ? allMenusItems : allItems}
                handleCartQuantityChange={handleCartQuantityChange}
                loadCartItemForEditing={loadCartItemForEditing}
                handleDeleteCartItem={handleDeleteCartItem}
                handleCartItemCommentChange={handleCartItemCommentChange}
                handleCourseTagChange={handleCourseTagChange}
                handleCartClientIndexChange={handleCartClientIndexChange}
              />
            </div>
            <div className="order-taker__right-column-footer">
              <div className="wide-footer-total-row">
                <span>Total:</span>
                <span>${cartSummaryTotal.toLocaleString('es-CL', { minimumFractionDigits: 0 })}</span>
              </div>
              {renderFooterButtons()}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="order-taker__top-bar">
            <GlobalSearchBar
              items={allItems}
              allMenusItems={allMenusItems}
              menus={allMenus}
              selectedMenuIndex={selectedMenuIndex}
              onMenuChange={handleMenuChange}
              onItemSelect={handleGlobalItemSelect}
              pedidoType={pedido_type}
              getItemStockInfo={getItemStockInfo}
              autoFocus={false}
            />
            {!disableLayoutToggle && (
              <button className="layout-toggle-button" onClick={handleToggleLayout} title="Cambiar a vista amplia" aria-label="Toggle layout mode">
                <FaTableColumns />
              </button>
            )}
          </div>
          {renderHeader()}
          {renderMain()}
          <div className="order-taker__footer">
            <div className="cart-summary-bar" onClick={() => setCartCollapsed(!cartCollapsed)} role="button" tabIndex={0}>
              <span className="cart-summary-bar__count">{cart.length} {cart.length === 1 ? 'item' : 'items'}</span>
              <span className="cart-summary-bar__total">${cartSummaryTotal.toLocaleString('es-CL', { minimumFractionDigits: 0 })}</span>
              <span className={`cart-summary-bar__chevron ${cartCollapsed ? '' : 'cart-summary-bar__chevron--expanded'}`}><ChevronLeft /></span>
            </div>
            {!cartCollapsed && (
              <Cart
                cart={cart}
                pedidoType={pedido_type}
                allItems={allMenusItems.length > 0 ? allMenusItems : allItems}
                handleCartQuantityChange={handleCartQuantityChange}
                loadCartItemForEditing={loadCartItemForEditing}
                handleDeleteCartItem={handleDeleteCartItem}
                handleCartItemCommentChange={handleCartItemCommentChange}
                handleCourseTagChange={handleCourseTagChange}
                handleCartClientIndexChange={handleCartClientIndexChange}
                reverseOrder={true}
              />
            )}
            {renderFooterButtons()}
          </div>
        </>
      )}
      {renderModals()}
    </div>
  );
};

export default OrderTaker;
